import EventHandler from '@pixelunion/events';
import Asyncview from '@pixelunion/shopify-asyncview';
import morphdom from 'morphdom';
import rimg from '@pixelunion/rimg-shopify';

export default class StaticCart {
  constructor(section) {
    this.events = new EventHandler();
    this.el = section.el;
    this.updateTimeout = null;
    this.dataErrorMsg = this.el.querySelector('[data-error-msg]');

    this._bindEvents();
  }

  _bindEvents() {
    const quantityInputs = this.el.querySelectorAll('[data-quantity-key]');
    const removeItemButtons = this.el.querySelectorAll('[data-remove]');

    quantityInputs.forEach(el => this.events.register(el, 'input', event => this._updateItemQuantity(event)));
    removeItemButtons.forEach(el => this.events.register(el, 'click', event => this._updateItemQuantity(event)));
  }

  _unbindEvents() {
    this.events.unregisterAll();
  }

  _updateItemQuantity(event) {
    event.preventDefault();
    const { currentTarget } = event;

    let quantity;
    let lineItemKey;

    if (currentTarget.hasAttribute('data-remove')) {
      quantity = 0;
      lineItemKey = event.currentTarget.dataset.lineItem;
    } else {
      quantity = currentTarget.value;
      if (quantity === '') {
        return;
      }
      lineItemKey = currentTarget.dataset.quantityKey;
    }

    // cancel any pending requests
    if (this.updateTimeout !== null) {
      clearTimeout(this.updateTimeout);
    }

    this.updateTimeout = setTimeout(() => {
      // Debounce to avoid firing requests while user is still entering number
      const thisTimeoutId = this.updateTimeout;

      /*
        When a non-integer value is used, Shopify throws an alert which doesn't have
        a user-friendly error message, so we'll handle this ourselves.
      */
      if (!Number.isInteger(Number(quantity))) {
        this.dataErrorMsg.classList.remove('hidden');
        return;
      }

      window.Shopify.changeItem(lineItemKey, quantity, cart => {
        // If another request is in progress, discard this update
        if (this.updateTimeout && this.updateTimeout !== thisTimeoutId) {
          return;
        }
        if (cart.item_count === 0) {
          // reload to show empty cart state
          window.location.reload();
          return;
        }
        this.updateCart(cart, thisTimeoutId);
      });
    }, 300);
  }

  updateCart(cart, timeoutId) {
    // Swap the cart contents to update discount labels.
    Asyncview.load(
      window.Theme.routes.cart_url,
      { view: '_ajax' },
    )
      .then(({ data, html }) => {
        // If another request is in progress, discard this update
        if (this.updateTimeout !== timeoutId) {
          return;
        }

        this._unbindEvents();

        // Inject new cart table contents
        const newTableContainer = document.createElement('div');
        newTableContainer.innerHTML = html.table;

        const cartTable = morphdom(
          this.el.querySelector('[data-cart-table]'),
          newTableContainer.querySelector('table'),
          {
            onBeforeElUpdated: (fromEl, toEl) => {
              // Skip images if src matches
              // - we don't want to reload lazy loaded images
              if (fromEl.tagName === 'IMG' && fromEl.src === toEl.src) {
                return false;
              }
              return true;
            },
          },
        );

        rimg.watch(cartTable);

        const cartDiscountContainer = this.el.querySelector('[data-cart-discount-container]');
        const totalPrice = this.el.querySelector('[data-total-price]');
        const cartItemCountWrap = document.querySelector('[data-header-cart-count]');

        // Inject new cart level discounts
        const newDiscountContainer = document.createElement('div');
        newDiscountContainer.innerHTML = html.discounts;
        cartDiscountContainer.innerHTML = newDiscountContainer.querySelector('[data-cart-discount-container]').innerHTML;

        // Update total
        totalPrice.innerHTML = data.totalPrice;

        if (cartItemCountWrap) {
          cartItemCountWrap.innerText = `(${data.itemCount})`;
        }

        this._bindEvents();
      })
      .catch(() => window.location.reload());
  }

  onSectionUnload() {
    this._unbindEvents();
  }
}
